import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import { compresserImage } from '../services/image';
import { authService } from '../services/auth.service';
import './Produits.css';

interface Produit {
  id: number;
  nom: string;
  description: string;
  codeBarres: string;
  reference: string;
  categorie: string;
  marque: string;
  prixOrdinaire: number;
  prixRevendeur: number;
  prixTechnicien: number;
  prixAchat: number;
  stockActuel: number;
  stockMinimum: number;
  tvaApplicable: boolean;
  actif: boolean;
  image?: string;
  notes: string;
}

interface Stats {
  total: number;
  enRupture: number;
  sousMinimum: number;
  valeurTotale: number;
}

const produitVide = {
  nom: '', description: '', codeBarres: '', reference: '',
  categorie: '', marque: '', unite: 'Pièce',
  prixOrdinaire: 0, prixRevendeur: 0, prixTechnicien: 0, prixAchat: 0,
  stockActuel: 0, stockMinimum: 5, stockMaximum: 100,
  tvaApplicable: true, image: '', notes: ''
};

// Catégories proposées par défaut. La liste réelle est enrichie automatiquement
// des catégories déjà saisies sur les produits (voir categoriesSuggestions).
const categoriesParDefaut = [
  'Réseau', 'Accessoires', 'Chargeur', 'Stockage',
  'Affichage', 'Périphérique', 'Caisse', 'Autre'
];

const fmt = (n: number) => Number(n).toLocaleString('fr-FR') + ' FCFA';

export default function Produits() {
  // La suppression d'un produit est réservée à l'admin et au gérant (le backend
  // l'impose aussi) : on masque le bouton pour les autres rôles.
  const peutSupprimer = ['admin', 'gerant'].includes(authService.getUser()?.role || '');
  const [produits, setProduits] = useState<Produit[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [recherche, setRecherche] = useState('');
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [produitSelectionne, setProduitSelectionne] = useState<Produit | null>(null);
  const [produitTransfert, setProduitTransfert] = useState<Produit | null>(null);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [form, setForm] = useState(produitVide);
  const [stockOp, setStockOp] = useState<'ajouter' | 'retirer'>('ajouter');
  const [stockQte, setStockQte] = useState(1);
  const [transfertQte, setTransfertQte] = useState(1);
  // Noms des boutiques réelles de l'entreprise (transfert de stock inter-boutiques).
  const [boutiques, setBoutiques] = useState<string[]>([]);
  const [boutiqueSrc, setBoutiqueSrc] = useState('');
  const [boutiqueDest, setBoutiqueDest] = useState('');
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [vue, setVue] = useState<'grille' | 'liste'>('grille');
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    chargerProduits();
    chargerStats();
    chargerBoutiques();
  }, []);

  const chargerBoutiques = async () => {
    try {
      const res = await api.get('/boutiques');
      setBoutiques((res.data as any[]).map(b => b.nom));
    } catch (e) {}
  };

  useEffect(() => {
    const timer = setTimeout(() => chargerProduits(), 300);
    return () => clearTimeout(timer);
  }, [recherche]);

  const chargerProduits = async () => {
    try {
      const res = await api.get('/produits', {
        params: recherche ? { recherche } : {}
      });
      setProduits(res.data);
    } catch (e) {
      setErreur('Impossible de charger les produits');
    } finally {
      setChargement(false);
    }
  };

  const chargerStats = async () => {
    try {
      const res = await api.get('/produits/stats');
      setStats(res.data);
    } catch (e) {}
  };

  const ouvrirCreation = () => {
    setEnEdition(null);
    setForm(produitVide);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirEdition = (p: Produit) => {
    setEnEdition(p.id);
    setForm({ ...produitVide, ...p });
    setErreur('');
    setShowModal(true);
  };

  const ouvrirStock = (p: Produit) => {
    setProduitSelectionne(p);
    setStockOp('ajouter');
    setStockQte(1);
    setShowStockModal(true);
  };

  const ouvrirSuppression = (p: Produit) => {
    setProduitSelectionne(p);
    setShowDeleteModal(true);
  };

  const ouvrirTransfert = (p: Produit) => {
    setProduitTransfert(p);
    setTransfertQte(1);
    // Défauts sur les vraies boutiques : la 1re en source, la 2e en destination.
    setBoutiqueSrc(boutiques[0] || '');
    setBoutiqueDest(boutiques[1] || '');
    setErreur('');
    setShowTransfertModal(true);
  };

  const calcPrix = (prixOrdinaire: number) => ({
    prixRevendeur: Math.round(prixOrdinaire * 0.85),
    prixTechnicien: Math.round(prixOrdinaire * 0.78),
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErreur('Veuillez choisir un fichier image (JPG, PNG…)');
      return;
    }
    try {
      const base64 = await compresserImage(file, 600, 0.72);
      setForm(f => ({ ...f, image: base64 }));
      setErreur('');
    } catch {
      setErreur("Impossible de traiter l'image choisie");
    } finally {
      // Permet de re-sélectionner le même fichier après un retrait.
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const sauvegarder = async () => {
    if (!form.nom || !form.prixOrdinaire) {
      setErreur('Le nom et le prix sont obligatoires');
      return;
    }
    try {
      if (enEdition) {
        await api.put(`/produits/${enEdition}`, form);
        setSucces('Produit modifié avec succès');
      } else {
        await api.post('/produits', form);
        setSucces('Produit créé avec succès');
      }
      setShowModal(false);
      chargerProduits();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const ajusterStock = async () => {
    if (!produitSelectionne) return;
    try {
      await api.put(`/produits/${produitSelectionne.id}/stock`, {
        quantite: stockQte,
        operation: stockOp,
      });
      setSucces(`Stock ${stockOp === 'ajouter' ? 'ajouté' : 'retiré'} avec succès`);
      setShowStockModal(false);
      chargerProduits();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur stock');
    }
  };

  const effectuerTransfert = async () => {
    if (!produitTransfert || !transfertQte) return;
    if (boutiqueSrc === boutiqueDest) {
      setErreur('La boutique source et destination doivent être différentes');
      return;
    }
    try {
      await api.post(`/produits/${produitTransfert.id}/transfert`, {
        quantite: transfertQte,
        boutiqueSrc,
        boutiqueDest,
      });
      setSucces(`Transfert de ${transfertQte} unité(s) de ${boutiqueSrc} vers ${boutiqueDest} effectué`);
      setShowTransfertModal(false);
      chargerProduits();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors du transfert');
    }
  };

  const supprimer = async () => {
    if (!produitSelectionne) return;
    try {
      await api.delete(`/produits/${produitSelectionne.id}`);
      setSucces('Produit supprimé');
      setShowDeleteModal(false);
      chargerProduits();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  const badgeStock = (p: Produit) => {
    if (p.stockActuel === 0) return <span className="stock-badge rupture">Rupture</span>;
    if (p.stockActuel <= p.stockMinimum) return <span className="stock-badge faible">Stock faible</span>;
    return <span className="stock-badge ok">{p.stockActuel} unités</span>;
  };

  if (chargement) return <div className="page-chargement">Chargement des produits...</div>;

  // Suggestions de catégories = défauts + catégories déjà utilisées (dédupliquées, triées).
  const categoriesSuggestions = Array.from(
    new Set([...categoriesParDefaut, ...produits.map(p => p.categorie).filter(Boolean)])
  ).sort((a, b) => a.localeCompare(b, 'fr'));

  return (
    <div className="produits-page">
      <div className="page-header">
        <div>
          <h1>Produits & Stocks</h1>
          <p>Gérez votre catalogue et vos niveaux de stock</p>
        </div>
        <button className="btn-primary" onClick={ouvrirCreation}>
          + Nouveau produit
        </button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-val">{stats.total}</span>
            <span className="stat-lbl">Produits</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#A32D2D' }}>{stats.enRupture}</span>
            <span className="stat-lbl">En rupture</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#854F0B' }}>{stats.sousMinimum}</span>
            <span className="stat-lbl">Stock faible</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ fontSize: 16, color: '#3B6D11' }}>
              {Number(stats.valeurTotale).toLocaleString('fr-FR')} F
            </span>
            <span className="stat-lbl">Valeur stock</span>
          </div>
        </div>
      )}

      <div className="search-bar">
        <input
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          placeholder="🔍 Rechercher par nom, code-barres ou catégorie..."
        />
        <div className="vue-toggle">
          <button className={vue === 'grille' ? 'active' : ''} onClick={() => setVue('grille')}>⊞ Grille</button>
          <button className={vue === 'liste' ? 'active' : ''} onClick={() => setVue('liste')}>☰ Liste</button>
        </div>
      </div>

      {/* VUE GRILLE */}
      {vue === 'grille' && (
        <div className="produits-grid">
          {produits.map(p => (
            <div key={p.id} className={`produit-card ${p.stockActuel === 0 ? 'rupture' : ''}`}>
              <div className="produit-card-header">
                <div className={`produit-icon ${p.image ? 'has-image' : ''}`}>
                  {p.image ? <img src={p.image} alt={p.nom} /> : '📦'}
                </div>
                <div className="produit-info">
                  <div className="produit-nom">{p.nom}</div>
                  <div className="produit-meta">
                    {p.categorie && <span className="produit-cat">{p.categorie}</span>}
                    {p.marque && <span className="produit-marque">{p.marque}</span>}
                  </div>
                </div>
                {badgeStock(p)}
              </div>
              <div className="produit-prix">
                <div className="prix-row">
                  <span className="prix-lbl">Ordinaire</span>
                  <span className="prix-val">{fmt(p.prixOrdinaire)}</span>
                </div>
                <div className="prix-row">
                  <span className="prix-lbl revendeur">Revendeur</span>
                  <span className="prix-val revendeur">{fmt(p.prixRevendeur)}</span>
                </div>
                <div className="prix-row">
                  <span className="prix-lbl technicien">Technicien</span>
                  <span className="prix-val technicien">{fmt(p.prixTechnicien)}</span>
                </div>
              </div>
              {p.codeBarres && <div className="produit-code">📊 {p.codeBarres}</div>}
              <div className="produit-actions">
                <button className="btn-stock" onClick={() => ouvrirStock(p)}>📦 Stock</button>
                <button className="btn-transfert" onClick={() => ouvrirTransfert(p)} title="Transfert inter-boutiques">🔄</button>
                <button className="btn-edit" onClick={() => ouvrirEdition(p)}>✏️</button>
                {peutSupprimer && (
                  <button className="btn-delete" onClick={() => ouvrirSuppression(p)}>🗑️</button>
                )}
              </div>
            </div>
          ))}
          <div className="produit-card card-add" onClick={ouvrirCreation}>
            <div className="add-icon">+</div>
            <div className="add-text">Nouveau produit</div>
          </div>
        </div>
      )}

      {/* VUE LISTE */}
      {vue === 'liste' && (
        <div className="produits-table">
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Catégorie</th>
                <th>Prix public</th>
                <th>Prix revendeur</th>
                <th>Stock</th>
                <th>TVA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {produits.map(p => (
                <tr key={p.id} className={p.stockActuel === 0 ? 'row-rupture' : ''}>
                  <td>
                    <div className="td-produit">
                      {p.image
                        ? <img className="td-thumb" src={p.image} alt={p.nom} />
                        : <span className="td-thumb vide">📦</span>}
                      <div>
                        <div className="td-nom">{p.nom}</div>
                        {p.codeBarres && <div className="td-code">{p.codeBarres}</div>}
                      </div>
                    </div>
                  </td>
                  <td>{p.categorie || '—'}</td>
                  <td className="td-prix">{fmt(p.prixOrdinaire)}</td>
                  <td className="td-prix revendeur">{fmt(p.prixRevendeur)}</td>
                  <td>{badgeStock(p)}</td>
                  <td>{p.tvaApplicable ? '18%' : 'Exo.'}</td>
                  <td>
                    <div className="td-actions">
                      <button onClick={() => ouvrirStock(p)} title="Ajuster stock">📦</button>
                      <button onClick={() => ouvrirTransfert(p)} title="Transfert">🔄</button>
                      <button onClick={() => ouvrirEdition(p)} title="Modifier">✏️</button>
                      {peutSupprimer && (
                        <button onClick={() => ouvrirSuppression(p)} title="Supprimer">🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {produits.length === 0 && <div className="table-empty">Aucun produit trouvé</div>}
        </div>
      )}

      {/* MODAL CREATION / EDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{enEdition ? 'Modifier le produit' : 'Nouveau produit'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-section">Identification</div>
              <div className="form-group">
                <label>Nom du produit <span className="req">*</span></label>
                <input value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Ex: Modem 4G LTE" />
              </div>
              <div className="form-row-3">
                <div className="form-group">
                  <label>Catégorie</label>
                  <input
                    list="categories-produits"
                    value={form.categorie}
                    onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}
                    placeholder="Choisir ou saisir une catégorie" />
                  <datalist id="categories-produits">
                    {categoriesSuggestions.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Marque</label>
                  <input value={form.marque}
                    onChange={e => setForm(f => ({ ...f, marque: e.target.value }))}
                    placeholder="Ex: TP-Link" />
                </div>
                <div className="form-group">
                  <label>Unité</label>
                  <select value={form.unite}
                    onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}>
                    <option>Pièce</option>
                    <option>Lot</option>
                    <option>Boîte</option>
                    <option>Mètre</option>
                    <option>Kg</option>
                  </select>
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Code-barres</label>
                  <input value={form.codeBarres}
                    onChange={e => setForm(f => ({ ...f, codeBarres: e.target.value }))}
                    placeholder="Ex: 6201234567890"
                    style={{ fontFamily: 'monospace' }} />
                </div>
                <div className="form-group">
                  <label>Référence interne</label>
                  <input value={form.reference}
                    onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                    placeholder="Laisser vide = généré automatiquement" />
                  {!enEdition && !form.reference.trim() && (
                    <div className="champ-hint">Une référence sera générée selon la catégorie (ex. ELE-0001).</div>
                  )}
                </div>
              </div>

              <div className="form-section">Photo du produit</div>
              <div className="produit-image-upload">
                {form.image ? (
                  <div className="pi-apercu">
                    <img src={form.image} alt="Aperçu du produit" />
                    <div className="pi-actions">
                      <button type="button" className="btn-secondary"
                        onClick={() => imageInputRef.current?.click()}>
                        Changer la photo
                      </button>
                      <button type="button" className="btn-retirer-photo"
                        onClick={() => setForm(f => ({ ...f, image: '' }))}>
                        Retirer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pi-vide" onClick={() => imageInputRef.current?.click()}>
                    <div className="pi-icone">📷</div>
                    <div className="pi-texte">Ajouter une photo</div>
                    <div className="pi-hint">JPG ou PNG — redimensionnée automatiquement</div>
                  </div>
                )}
                <input ref={imageInputRef} type="file" accept="image/*"
                  style={{ display: 'none' }} onChange={handleImageUpload} />
              </div>

              <div className="form-section">Tarification</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Prix d'achat (FCFA)</label>
                  <InputNombre
                    value={form.prixAchat}
                    onChange={val => setForm(f => ({ ...f, prixAchat: val }))}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Prix public Ordinaire (FCFA) <span className="req">*</span></label>
                  <InputNombre
                    value={form.prixOrdinaire}
                    onChange={val => {
                      const auto = calcPrix(val);
                      setForm(f => ({ ...f, prixOrdinaire: val, ...auto }));
                    }}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Prix Revendeur (-15%)</label>
                  <InputNombre
                    value={form.prixRevendeur}
                    onChange={val => setForm(f => ({ ...f, prixRevendeur: val }))}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Prix Technicien (-22%)</label>
                  <InputNombre
                    value={form.prixTechnicien}
                    onChange={val => setForm(f => ({ ...f, prixTechnicien: val }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="prix-info">
                💡 Les prix Revendeur et Technicien sont calculés automatiquement.
                Vous pouvez les ajuster manuellement.
              </div>

              <div className="form-section">Stock</div>
              <div className="form-row-3">
                <div className="form-group">
                  <label>Stock initial</label>
                  <InputNombre
                    value={form.stockActuel}
                    onChange={val => setForm(f => ({ ...f, stockActuel: val }))}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Stock minimum (alerte)</label>
                  <InputNombre
                    value={form.stockMinimum}
                    onChange={val => setForm(f => ({ ...f, stockMinimum: val }))}
                    placeholder="5"
                  />
                </div>
                <div className="form-group">
                  <label>Stock maximum</label>
                  <InputNombre
                    value={form.stockMaximum}
                    onChange={val => setForm(f => ({ ...f, stockMaximum: val }))}
                    placeholder="100"
                  />
                </div>
              </div>

              <div className="form-section">Options</div>
              <label className="toggle-row">
                <span>TVA 18% applicable</span>
                <input type="checkbox" checked={form.tvaApplicable}
                  onChange={e => setForm(f => ({ ...f, tvaApplicable: e.target.checked }))} />
              </label>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Informations complémentaires..."
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 7, fontFamily: 'inherit', fontSize: 13 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={sauvegarder}>
                {enEdition ? 'Enregistrer' : 'Créer le produit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL STOCK */}
      {showStockModal && produitSelectionne && (
        <div className="modal-overlay" onClick={() => setShowStockModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📦 Ajuster le stock</h2>
              <button className="modal-close" onClick={() => setShowStockModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="stock-actuel-info">
                <span>Stock actuel de <strong>{produitSelectionne.nom}</strong></span>
                <span className="stock-val">{produitSelectionne.stockActuel} unités</span>
              </div>
              <div className="stock-op-toggle">
                <button className={stockOp === 'ajouter' ? 'active' : ''}
                  onClick={() => setStockOp('ajouter')}>+ Ajouter</button>
                <button className={stockOp === 'retirer' ? 'active danger' : ''}
                  onClick={() => setStockOp('retirer')}>- Retirer</button>
              </div>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label>Quantité à {stockOp === 'ajouter' ? 'ajouter' : 'retirer'}</label>
                <InputNombre
                  value={stockQte}
                  onChange={val => setStockQte(val)}
                  placeholder="1"
                  min={1}
                />
              </div>
              <div className="stock-preview">
                Nouveau stock : <strong>
                  {stockOp === 'ajouter'
                    ? produitSelectionne.stockActuel + stockQte
                    : Math.max(0, produitSelectionne.stockActuel - stockQte)
                  } unités
                </strong>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowStockModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={ajusterStock}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TRANSFERT */}
      {showTransfertModal && produitTransfert && (
        <div className="modal-overlay" onClick={() => setShowTransfertModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔄 Transfert de stock</h2>
              <button className="modal-close" onClick={() => setShowTransfertModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="transfert-produit-info">
                <div className="tpi-nom">{produitTransfert.nom}</div>
                <div className="tpi-stock">
                  Stock disponible : <strong>{produitTransfert.stockActuel} unité(s)</strong>
                </div>
              </div>

              {boutiques.length < 2 ? (
                <div className="alert-erreur" style={{ marginTop: 8 }}>
                  ⚠️ Le transfert nécessite au moins deux boutiques.
                  Créez-en une autre dans le menu « Boutiques ».
                </div>
              ) : (
                <>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Boutique source</label>
                      <select value={boutiqueSrc} onChange={e => setBoutiqueSrc(e.target.value)}>
                        <option value="">— Choisir —</option>
                        {boutiques.map(b => <option key={b}>{b}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Boutique destination</label>
                      <select value={boutiqueDest} onChange={e => setBoutiqueDest(e.target.value)}>
                        <option value="">— Choisir —</option>
                        {boutiques.map(b => <option key={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>

                  {boutiqueSrc && boutiqueDest && (
                    <div className="transfert-fleche">
                      {boutiqueSrc} → {boutiqueDest}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Quantité à transférer</label>
                    <InputNombre
                      value={transfertQte}
                      onChange={val => setTransfertQte(val)}
                      placeholder="1"
                      min={1}
                      max={produitTransfert.stockActuel}
                    />
                  </div>

                  {transfertQte > 0 && boutiqueSrc && boutiqueDest && boutiqueSrc !== boutiqueDest && (
                    <div className="transfert-preview">
                      Stock restant après transfert :
                      <strong> {Math.max(0, produitTransfert.stockActuel - transfertQte)} unité(s)</strong>
                    </div>
                  )}

                  {boutiqueSrc && boutiqueDest && boutiqueSrc === boutiqueDest && (
                    <div className="alert-erreur" style={{ marginTop: 8 }}>
                      ⚠️ La boutique source et destination doivent être différentes
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowTransfertModal(false)}>
                Annuler
              </button>
              <button className="btn-primary" onClick={effectuerTransfert}
                disabled={transfertQte > produitTransfert.stockActuel || boutiqueSrc === boutiqueDest}>
                🔄 Confirmer le transfert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDeleteModal && produitSelectionne && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer le produit</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous supprimer <strong>{produitSelectionne.nom}</strong> ?
              Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button className="btn-danger" onClick={supprimer}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}